import { useCallback, useEffect, useRef, useState } from "react";
import urls from "./global/constants/UrlConstants";
import { BrowserRouter, Route, Router, Switch } from "react-router-dom";
import LandingPage from "./screens/LandingPages/LandingPage";
import history from "./utils/history";
import Layout from "./Shared/Layout/Layout";
import Notifications from "./utils/notifications";
import { ThemeProvider } from "@mui/material/styles";
import { theme } from "./utils/styles";
import { generateToken, messaging } from "./notifications/firebase";
import { onMessage } from "firebase/messaging";
import toast, { Toaster } from "react-hot-toast";
import {
  ackLeadReminder,
  getAllTodayReminders,
  getNotification,
} from "./utils/PushNotification/pushNotificationService";
import { RootState } from "./utils/store";
import { useAppDispatch } from "./utils/hooks";
import { addNotification } from "./redux/notificationDataSlice";
import { useSelector } from "react-redux";
import { connectLeadRealtime, subscribeLeadTableRefresh, isLeadRealtimeForUser, notificationBelongsToUser, LeadRealtimePayload } from "./notifications/leadRealtime";
import { ReminderPopup, ReminderPopupItem } from "./ui/ReminderPopup";

const REMINDERS_SEEN_KEY = "todayRemindersSeenDate";
const LIVE_SHOWN_KEY = "pulse.shownReminderAlerts";
const REMINDER_TYPE = /reminder/i;

function isReminderPayload(payload: LeadRealtimePayload | { data?: Record<string, unknown>; title?: string; type?: string }) {
  const type = String(payload?.type || payload?.data?.type || "");
  const title = String(payload?.title || payload?.data?.title || "");
  return REMINDER_TYPE.test(type) || REMINDER_TYPE.test(title);
}

function isLiveReminderAlert(payload: {
  type?: unknown;
  title?: unknown;
  data?: Record<string, unknown>;
}) {
  const blob = `${payload?.type || payload?.data?.type || ""} ${
    payload?.title || payload?.data?.title || ""
  }`.toLowerCase();
  return /overdue|duesoon|5 minute/.test(blob);
}

function reminderAlreadyShown(key: string, live: Set<string>) {
  if (!key) return false;
  if (live.has(key)) return true;
  try {
    const raw = JSON.parse(sessionStorage.getItem(LIVE_SHOWN_KEY) || "[]");
    return Array.isArray(raw) && raw.includes(key);
  } catch {
    return false;
  }
}

function markReminderShown(key: string, live: Set<string>) {
  if (!key) return;
  live.add(key);
  try {
    const raw = JSON.parse(sessionStorage.getItem(LIVE_SHOWN_KEY) || "[]");
    const next = Array.from(
      new Set([...(Array.isArray(raw) ? raw : []), key])
    ).slice(-80);
    sessionStorage.setItem(LIVE_SHOWN_KEY, JSON.stringify(next));
  } catch {
    // ignore storage errors
  }
}

function parseJsonObject(value: unknown): Record<string, unknown> {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (typeof value !== "string" || !value.trim()) return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch {
    return {};
  }
}

function reminderHeadingFrom(type: string, title: string) {
  const blob = `${type} ${title}`.toLowerCase();
  if (blob.includes("overdue")) return "Your reminder is overdue";
  if (blob.includes("5 minute") || blob.includes("duesoon")) {
    return "Reminder in 5 minutes";
  }
  if (blob.includes("added") || blob.includes("set")) {
    return "Follow-up reminder set";
  }
  return String(title || "Reminder").replace(/^⏰\s*/, "").trim() || "Reminder";
}

function lineValue(body: string, label: string) {
  const match = String(body || "").match(
    new RegExp(`^${label}:\\s*(.+)$`, "im")
  );
  return match ? match[1].trim() : "";
}

function reminderItemFromPayload(payload: {
  title?: unknown;
  body?: unknown;
  text?: unknown;
  data?: Record<string, unknown>;
}): ReminderPopupItem {
  const data = payload?.data && typeof payload.data === "object" ? payload.data : {};
  const nested = parseJsonObject(data.payload);
  const pick = (key: string) =>
    String(data[key] || nested[key] || "").trim();
  const body = String(
    payload?.body || payload?.text || data.body || data.text || ""
  );
  const due =
    [pick("reminderDate"), pick("reminderTime")].filter(Boolean).join(" ") ||
    lineValue(body, "Due");
  return {
    title: String(payload?.title || data.title || ""),
    body,
    leadName: pick("leadName") || lineValue(body, "Lead"),
    uid: pick("uid") || lineValue(body, "Lead ID"),
    leadId: pick("leadId"),
    leadPhone: pick("leadPhone") || lineValue(body, "Mobile"),
    reminderTitle: pick("reminderTitle") || lineValue(body, "Reminder"),
    reminderId: pick("reminderId"),
    due,
  };
}

function App() {
  const dispatch = useAppDispatch();
  const stateUserId = useSelector((state: RootState) => state.auth.userId);
  const audioUnlocked = useRef(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [todayReminders, setTodayReminders] = useState<ReminderPopupItem[]>([]);
  const [remindersOpen, setRemindersOpen] = useState(false);
  const [reminderHeading, setReminderHeading] = useState("Today's Reminders");
  const markSeenOnClose = useRef(false);
  const shownLiveReminders = useRef<Set<string>>(new Set());

  const playSound = () => {
    // Never call play() before a user gesture — browsers reject it and can leave
    // an Uncaught (in promise) NotAllowedError in the console.
    if (!audioUnlocked.current) return;
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio("/notification-sound.mp3");
      }
      const audio = audioRef.current;
      audio.currentTime = 0;
      void audio.play().catch(() => {});
    } catch {
      // ignore
    }
  };

  const closeReminders = useCallback(() => {
    const ids = todayReminders
      .map((item) => String(item.reminderId || "").trim())
      .filter(Boolean);
    setRemindersOpen(false);
    setTodayReminders([]);
    if (markSeenOnClose.current) {
      const today = new Date().toISOString().split("T")[0];
      localStorage.setItem(REMINDERS_SEEN_KEY, today);
      markSeenOnClose.current = false;
    }
    if (ids.length) {
      void ackLeadReminder(ids);
    }
  }, [todayReminders]);

  const openReminderPopup = useCallback(
    (items: ReminderPopupItem[], heading: string, markSeen: boolean) => {
      const next = (items || []).filter((item) => item?.title || item?.body);
      if (!next.length) return;
      markSeenOnClose.current = markSeen;
      setReminderHeading(heading);
      setTodayReminders(next);
      setRemindersOpen(true);
    },
    []
  );

  useEffect(() => {
    const unlock = () => {
      if (audioUnlocked.current) return;
      audioUnlocked.current = true;
      try {
        const audio = new Audio("/notification-sound.mp3");
        audio.muted = true;
        void audio
          .play()
          .then(() => {
            audio.pause();
            audio.muted = false;
            audioRef.current = audio;
          })
          .catch(() => {});
      } catch {
        // ignore
      }
    };
    document.addEventListener("pointerdown", unlock, { once: true });
    document.addEventListener("keydown", unlock, { once: true });
    return () => {
      document.removeEventListener("pointerdown", unlock);
      document.removeEventListener("keydown", unlock);
    };
  }, []);

  useEffect(() => {
    if (!stateUserId) return;

    const today = new Date().toISOString().split("T")[0];
    if (localStorage.getItem(REMINDERS_SEEN_KEY) === today) return;

    let cancelled = false;

    const loadReminders = async () => {
      try {
        const reminders = await getAllTodayReminders();
        const rawList = Array.isArray(reminders?.data) ? reminders.data : [];
        const list = rawList
          .filter(
            (item: { userId?: unknown }) =>
              String(item?.userId || "").trim() === String(stateUserId)
          )
          .map(
            (item: {
              title?: string;
              body?: string;
              leadName?: string;
              uid?: string;
              leadPhone?: string;
              reminderTitle?: string;
              reminderId?: string;
              date?: string;
              time?: string;
            }) => ({
              title: item.title,
              body: item.body,
              leadName: item.leadName,
              uid: item.uid,
              leadPhone: item.leadPhone,
              reminderTitle: item.reminderTitle || item.title,
              reminderId: item.reminderId,
              due: [item.date, item.time].filter(Boolean).join(" "),
            })
          );
        if (cancelled || !list.length) {
          if (!cancelled) localStorage.setItem(REMINDERS_SEEN_KEY, today);
          return;
        }

        markSeenOnClose.current = true;
        setReminderHeading("Today's Reminders");
        setTodayReminders(list);
        setRemindersOpen(true);
        playSound();
      } catch {
        // reminder fetch failures should not block the app
      }
    };

    loadReminders();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateUserId]);

  useEffect(() => {
    if (!stateUserId) return;
    connectLeadRealtime(String(stateUserId));
    const unsub = subscribeLeadTableRefresh(async (payload) => {
      if (!isLeadRealtimeForUser(payload, String(stateUserId))) return;
      if (isLiveReminderAlert(payload) && isReminderPayload(payload)) {
        const key = String(
          payload?.data?.reminderId ||
            payload?.data?.leadId ||
            payload?.leadId ||
            payload?.body ||
            payload?.title ||
            ""
        );
        if (!reminderAlreadyShown(key, shownLiveReminders.current)) {
          markReminderShown(key, shownLiveReminders.current);
          openReminderPopup(
            [reminderItemFromPayload(payload)],
            reminderHeadingFrom(
              String(payload?.type || ""),
              String(payload?.title || "")
            ),
            false
          );
          playSound();
        }
      } else if (!isReminderPayload(payload)) {
        playSound();
      }
      try {
        const notificationData = await getNotification();
        dispatch(addNotification(notificationData?.data?.flat() ?? []));
      } catch {
        // ignore
      }
    });
    return () => {
      unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateUserId, dispatch]);

  useEffect(() => {
    if (!stateUserId || !messaging) return;

    let unsubscribe: (() => void) | undefined;

    try {
      void generateToken().catch(() => {});
      unsubscribe = onMessage(messaging, async (payload) => {
        try {
          const isForCurrentUser = notificationBelongsToUser(
            {
              userIds: payload.data?.userIds ?? payload.data?.userId,
              userId: payload.data?.userId,
              type: payload.data?.type,
              title: payload.data?.title ?? payload.notification?.title,
              text: payload.data?.text ?? payload.data?.body,
            },
            String(stateUserId)
          );
          if (!isForCurrentUser) return;

          playSound();
          const notificationData = await getNotification();
          dispatch(addNotification(notificationData?.data?.flat() ?? []));
          const reminderLike = isReminderPayload({
            type: String(payload.data?.type || ""),
            title: String(payload.data?.title || ""),
            data: payload.data as Record<string, unknown>,
          });
          if (reminderLike && isLiveReminderAlert({
            type: payload.data?.type,
            title: payload.data?.title ?? payload.notification?.title,
            data: payload.data as Record<string, unknown>,
          })) {
            const nestedPush = parseJsonObject(payload.data?.payload);
            const key = String(
              payload.data?.reminderId ||
                nestedPush.reminderId ||
                payload.data?.leadId ||
                nestedPush.leadId ||
                payload.data?.body ||
                payload.data?.title ||
                ""
            );
            if (!reminderAlreadyShown(key, shownLiveReminders.current)) {
              markReminderShown(key, shownLiveReminders.current);
              openReminderPopup(
                [
                  reminderItemFromPayload({
                    title: payload.data?.title ?? payload.notification?.title,
                    body: payload.data?.body ?? payload.data?.text,
                    text: payload.data?.text,
                    data: payload.data as Record<string, unknown>,
                  }),
                ],
                reminderHeadingFrom(
                  String(payload.data?.type || ""),
                  String(payload.data?.title || payload.notification?.title || "")
                ),
                false
              );
            }
          } else if (!reminderLike) {
            toast(
              <div style={{ fontSize: "12px" }}>
                <div>{payload.data?.title ?? ""}</div>
                <div>{payload.data?.text ?? ""}</div>
                <p
                  dangerouslySetInnerHTML={{
                    __html: payload.data?.body ?? "",
                  }}
                />
              </div>,
              { duration: 5000, position: "top-right" }
            );
          }
        } catch {
          // ignore notification errors
        }
      });
    } catch {
      // firebase messaging unavailable — app must still render
    }

    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [stateUserId, dispatch]);

  return (
    <>
      <ThemeProvider theme={theme}>
        <BrowserRouter>
          <Router history={history}>
            <Switch>
              <Route
                exact
                path={[
                  urls.landingViewPath,
                  urls.forgotPasswordViewPath,
                  urls.loginViewPath,
                  urls.changePasswordViewPath,
                  urls.SignInViewPath,
                  urls.mobileVerificationViewPath,
                  urls.signUpViewPath,
                  urls.onBoadingSelectIndustryViewPath,
                  urls.resetPasswordLinkViewPath,
                ]}
                component={LandingPage}
              />
              <Layout />
            </Switch>
          </Router>
        </BrowserRouter>
        <Notifications />
        <Toaster position="top-right" reverseOrder={false} />
        <ReminderPopup
          open={remindersOpen}
          items={todayReminders}
          heading={reminderHeading}
          onOk={closeReminders}
        />
      </ThemeProvider>
    </>
  );
}

export default App;
